import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { ProjectsTable } from './projects-table.tsx'
import { MainContent } from '../../components/main-content.tsx'
import { MainContentHeader } from '../../components/main-content-header.tsx'
import { MainContentBody } from '../../components/main-content-body.tsx'
import { Layout } from '../../components/layout.tsx'
import { ProjectFormModal, ConfirmDialog } from '../../components/index.ts'
import {
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} from '../../redux/hooks.ts'
import type { ProjectModel, ProjectCreate, ProjectUpdate } from '@itemize/server/models'

export const ProjectsPage: FC = () => {
  const navigate = useNavigate()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<ProjectModel | null>(null)

  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation()
  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation()
  const [deleteProject, { isLoading: isDeleting }] = useDeleteProjectMutation()

  const handleAddProject = () => {
    setIsCreateModalOpen(true)
  }

  const handleCreateProject = async (data: ProjectCreate | ProjectUpdate) => {
    // For create, we know it's ProjectCreate type since we're in create mode
    const project = await createProject(data as ProjectCreate).unwrap()
    setIsCreateModalOpen(false)
    navigateToProject(project)
  }

  const handleView = (project: ProjectModel) => {
    navigateToProject(project)
  }

  const navigateToProject = (project: ProjectModel) => {
    navigate(`/clients/${project.clientId}/projects/${project.id}`)
  }

  const handleEdit = (project: ProjectModel) => {
    setSelectedProject(project)
    setIsEditModalOpen(true)
  }

  const handleUpdateProject = async (data: ProjectCreate | ProjectUpdate) => {
    if (!selectedProject) return
    // For update, we know it's ProjectUpdate type since we're in edit mode
    await updateProject({ id: selectedProject.id, data: data as ProjectUpdate }).unwrap()
    setIsEditModalOpen(false)
    setSelectedProject(null)
  }

  const handleDelete = (project: ProjectModel) => {
    setSelectedProject(project)
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedProject) return
    await deleteProject(selectedProject.id).unwrap()
    setIsDeleteDialogOpen(false)
    setSelectedProject(null)
  }

  const handleToggleStatus = async (project: ProjectModel) => {
    await updateProject({
      id: project.id,
      data: { status: project.status === 'open' ? 'closed' : 'open' },
    }).unwrap()
  }

  return (
    <Layout>
      <MainContent>
        <MainContentHeader
          breadcrumbs={[{ label: 'Projects', href: '/projects' }]}
          buttons={
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProject}>
              Add Project
            </Button>
          }
        />
        <MainContentBody>
          <ProjectsTable
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleStatus={handleToggleStatus}
          />
        </MainContentBody>
      </MainContent>

      <ProjectFormModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateProject}
        title="Create New Project"
        submitText="Create Project"
        isLoading={isCreating}
      />

      <ProjectFormModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedProject(null)
        }}
        onSubmit={handleUpdateProject}
        initialData={
          selectedProject
            ? {
                name: selectedProject.name,
                clientId: selectedProject.clientId,
                description: selectedProject.description || undefined,
              }
            : undefined
        }
        title="Edit Project"
        submitText="Update Project"
        isLoading={isUpdating}
      />

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setSelectedProject(null)
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${selectedProject?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
      />
    </Layout>
  )
}
